import { z } from "zod";

import { VibeDeploymentStatusReadSchema } from "./status-read.schemas";

/**
 * How many of an app's most recent attempts the list read carries.
 *
 * A BOUND, and it is the whole reason this read stays affordable: an app with
 * 500 versions must not return 500 rows into a grid that draws a dozen ticks.
 * The window is per app, so the read's deployment ceiling is
 * `12 x (apps the caller can see)`.
 *
 * Twelve because the card's strip is what consumes it and twelve is what fits:
 * at 5px a tick plus a 2px gap that is 82px, inside a panel roughly 300px wide
 * on a 340px card, leaving room for the counts beside it. Not a product rule —
 * change it here and both ends move together, because the server bounds the
 * query with this constant and the console never slices.
 */
export const VIBE_APP_DEPLOYMENT_HISTORY_LIMIT = 12;

/**
 * One attempt in the app's recent history — the smallest thing a tick needs.
 *
 * Deliberately NOT a `VibeAppDeploymentSummary`. A tick renders a colour and a
 * tooltip naming a version; carrying the sha, the error, the screenshot and the
 * timestamps for each of twelve rows would multiply this read's payload to buy
 * a strip of 5px bars nothing else on the card reads.
 *
 * `status` rather than a pre-computed colour or an `ok` boolean: there are
 * EIGHT statuses and a binary cannot express the three a card must draw
 * differently (shipped, failed, still running). `vibeDeploymentOutcome` in
 * `@nexus/types` is the exhaustive classifier both ends share — the counts
 * below are computed with it, so a tick coloured from it can never disagree
 * with the rate printed beside it.
 */
export const VibeAppDeploymentHistoryEntrySchema = z.object({
  /// User-facing monotonic version (`v{n}`) of this attempt. Carried per entry
  /// rather than derived by counting backwards from the newest: versions are
  /// never reused but they ARE gapped by soft-deleted rows, so arithmetic over
  /// the array's index labels ticks with versions that never existed.
  versionNumber: z.number().int(),
  status: VibeDeploymentStatusReadSchema
});

export type VibeAppDeploymentHistoryEntryDto = z.infer<typeof VibeAppDeploymentHistoryEntrySchema>;

/**
 * The app's deploy history: a bounded window of recent attempts, plus lifetime
 * counts over ALL of them.
 *
 * The two halves answer different questions and are measured over different
 * populations, which is the whole reason they are separate fields:
 * `recent` is the last {@link VIBE_APP_DEPLOYMENT_HISTORY_LIMIT} attempts,
 * `total`/`succeeded`/`failed`/`inFlight` are every attempt the app has ever
 * had. **A caption built by counting `recent` is a different number** — it
 * silently reads "12 deploys" for an app with 500.
 *
 * 🔴 **THE THREE COUNTS PARTITION `total` BY CONSTRUCTION, AND THAT IS WHY ALL
 * THREE ARE PUBLISHED RATHER THAN TWO PLUS A SUBTRACTION.** They come from one
 * `groupBy` over an exhaustive classification of `VibeDeploymentStatus`, so
 * `succeeded + failed + inFlight === total` always. Publishing two and asking a
 * consumer to subtract encodes the belief that the sets partition — the belief
 * that breaks the day a ninth status lands, silently and in the reader's
 * favour. Counted, never subtracted.
 *
 * A success rate is `succeeded / (succeeded + failed)`. **`inFlight` belongs in
 * neither half of that fraction**: an attempt still building has no outcome,
 * and putting it in the denominator makes an app's rate dip every time someone
 * pushes.
 *
 * 🔴 **`succeeded` IS NOT "attempts that reached HEALTHY", AND NO FIELD HERE
 * IS.** `vibeDeploymentOutcome`'s own docblock carries the mechanism: the only
 * witness that a row ever occupied the live slot is its CURRENT status, and two
 * transitions destroy it, so a version that served real traffic and was then
 * rolled back or pulled counts `failed`. That is the pessimistic reading and it
 * is the honest one — but a surface that captions it "reached HEALTHY" is
 * making a claim the database cannot support.
 */
export const VibeAppDeploymentHistorySchema = z.object({
  /// NEWEST FIRST, matching the order the list read already sorts deployments
  /// in. A strip that draws oldest-to-newest left-to-right reverses this at the
  /// render, deliberately and visibly, rather than the server guessing which
  /// direction a consumer wants.
  recent: z.array(VibeAppDeploymentHistoryEntrySchema),
  /// Every attempt this app has ever had, soft-deleted rows excluded. `0` for
  /// an app nobody has deployed — a real state with its own empty rendering,
  /// never a reason to hide the panel.
  total: z.number().int(),
  /// Attempts that are serving or were cleanly handed over — see the caveat
  /// above before naming this on a screen.
  succeeded: z.number().int(),
  /// Attempts that are terminal and not serving.
  failed: z.number().int(),
  /// Attempts with no outcome yet: building, awaiting approval, or deploying.
  inFlight: z.number().int()
});

export type VibeAppDeploymentHistoryDto = z.infer<typeof VibeAppDeploymentHistorySchema>;
